import { useEffect, useRef } from "react";
import { Phone, ChevronDown, Download } from "lucide-react";
import { SITE } from "../../lib/data";

/**
 * Hero — autoplay drone video (kept exactly as-is per user direction).
 * No badge above headline. No small uppercase overlines. Clean, editorial.
 */
export default function Hero() {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) return;

    let started = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!started) {
              video.setAttribute("preload", "auto");
              try { video.load(); } catch (_e) { /* noop */ }
              started = true;
            }
            const p = video.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          } else if (started) {
            video.pause();
          }
        });
      },
      { rootMargin: "0px", threshold: 0.05 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0B0F0D]"
    >
      <div className="absolute inset-0 overflow-hidden">
        <video
          ref={videoRef}
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          // eslint-disable-next-line react/no-unknown-property
          webkit-playsinline="true"
          // eslint-disable-next-line react/no-unknown-property
          x5-playsinline="true"
          disablePictureInPicture
          controls={false}
          preload="none"
          poster="/hero-poster.jpg"
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 w-auto h-auto min-w-[110%] min-h-[110%] object-cover pointer-events-none"
          style={{ transform: "translate(-50%, -50%) scale(1.1)" }}
        >
          <source src="/hero-mobile.mp4" media="(max-width: 768px)" type="video/mp4" />
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F0C]/45 via-[#0A0F0C]/20 to-[#0A0F0C]/85" />
      </div>

      <div className="relative z-10 min-h-[100svh] flex flex-col justify-between px-6 sm:px-10 lg:px-16 pt-36 sm:pt-40 lg:pt-48 pb-12 lg:pb-16">
        <div className="max-w-6xl">
          <h1
            data-testid="hero-title"
            className="font-serif-display text-[#F7F5F0] text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tighter font-light"
          >
            500 Acres of <em className="font-normal">Lush Green,</em>
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            Overlooking the Lake.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 sm:mt-10 max-w-2xl text-[#F7F5F0]/90 text-base sm:text-lg md:text-xl font-light leading-relaxed"
          >
            Mumbai to Amarja Hills — 99 KM
            <span className="mx-3 text-[#E2CFA1]/70">|</span>
            Pune to Amarja Hills — 49 KM
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 sm:gap-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <button
              data-testid="hero-cta-enquire"
              onClick={() => scrollTo("enquiry")}
              className="btn-ghost bg-[#F7F5F0] text-[#1A362D] border-[#F7F5F0] hover:bg-[#987F53] hover:text-[#F7F5F0] hover:border-[#987F53] w-full sm:w-auto"
            >
              Request Pricing
            </button>
            <a
              data-testid="hero-cta-call"
              href={`tel:${SITE.telHref}`}
              className="btn-ghost w-full sm:w-auto"
            >
              <Phone className="w-4 h-4" />
              {SITE.phone}
            </a>
            <a
              data-testid="hero-cta-brochure"
              href="/brochure/Amarja-Global-Investor-Brochure.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost w-full sm:w-auto"
            >
              <Download className="w-4 h-4" />
              Brochure
            </a>
          </div>

          <button
            data-testid="hero-scroll-indicator"
            onClick={() => scrollTo("projects")}
            className="hidden md:flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#F7F5F0]/70 hover:text-[#F7F5F0]"
          >
            Our Completed Work
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
}
